import { cachedAxiosGet } from '../utils/http';
import moralisClient from '../utils/moralis';

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
const BACKEND_BASE =
  API_BASE || "https://crypto-scan-tkoq5.ondigitalocean.app";

/**
 * Fetch trending tokens
 * @param {string} chain - Optional chain ID filter
 * @param {number} limit - Number of results to return
 * @param {boolean} enrich - Whether to fetch prices/logos individually (slow)
 * @returns {Promise} Promise resolving to trending tokens data
 */
// services/api.js
export const getTrendingTokens = async (chain = "", limit = 100, enrich = true) => {
  try {
    // Only add chain parameter if it's not empty
    const chainParam = chain ? `&chain=${chain}` : "";
    const url = `https://deep-index.moralis.io/api/v2.2/tokens/trending?limit=${limit}${chainParam}`;
    
    // Use cachedAxiosGet with moralisClient for rotation & caching
    const response = await cachedAxiosGet(moralisClient, url, {}, 60000);
    const data = response.data;
    
    const items = Array.isArray(data) ? data : data.result || [];

    if (!enrich) return items;

    const pickAddr = (t) =>
      t?.token_address ||
      t?.address ||
      t?.tokenAddress ||
      t?.baseToken?.address ||
      t?.mint ||
      "";
    const pickChain = (t) =>
      chain ||
      t?.chain ||
      t?.chainId ||
      t?.chain_id ||
      t?.network ||
      "";
    const pickLogo = (t, seed) =>
      t?.logo ||
      t?.logo_url ||
      t?.tokenLogo ||
      t?.image ||
      t?.icon ||
      `https://api.dicebear.com/7.x/identicon/svg?seed=${encodeURIComponent(
        seed || "tkn"
      )}`;

    const enriched = new Array(items.length);
    let i = 0;
    const concurrency = 6;
    async function worker() {
      while (i < items.length) {
        const idx = i++;
        const t = items[idx];
        const addr = String(pickAddr(t) || "").toLowerCase();
        const chIn = String(pickChain(t) || "").toLowerCase();
        const seed = addr || t?.symbol || "tkn";
        let price = null;
        try {
          if (addr && chIn) {
            const p = await getTokenPrice(chIn, addr);
            const pv =
              p?.usdPrice ?? p?.priceUsd ?? p?.price_usd ?? p?.price ?? null;
            price = isFinite(Number(pv)) ? Number(pv) : null;
          }
        } catch {}
        const logo = pickLogo(t, seed);
        enriched[idx] = { ...t, priceUsd: price, logo };
      }
    }
    await Promise.all(Array.from({ length: concurrency }, worker));
    return enriched;
  } catch (error) {
    console.error("Error fetching trending tokens:", error);
    throw error;
  }
};

/**
 * Search for tokens
 * @param {string} query - Search query
 * @param {Array} chains - Array of chain IDs to search
 * @param {number} limit - Number of results to return
 * @returns {Promise} Promise resolving to search results
 */
export const searchTokens = async (
  query,
  chains = ["eth", "solana", "bsc", "base"],
  limit = 20
) => {
  try {
    const chainsParam = chains.join(",");
    const url = `https://deep-index.moralis.io/api/v2.2/tokens/search?query=${encodeURIComponent(
      query
    )}&chains=${chainsParam}&limit=${limit}`;

    // Use cachedAxiosGet with moralisClient
    const response = await cachedAxiosGet(moralisClient, url, {}, 30000);
    const data = response.data;
    return data.result || [];
  } catch (error) {
    console.error("Error searching tokens:", error);
    throw error;
  }
};

/**
 * Get token price
 * @param {string} chainId - Chain ID
 * @param {string} tokenAddress - Token address
 * @returns {Promise} Promise resolving to token price data
 */
export const getTokenPrice = async (chainId, tokenAddress) => {
  try {
    // Convert chainId for Moralis API format if needed
    const chain = formatChainForApi(chainId);

    let moralis = null;
    try {
      const url = `https://deep-index.moralis.io/api/v2.2/tokens/${chain}/${tokenAddress}/price`;
      // Use moralisClient directly (or via cachedAxiosGet if we want caching)
      const response = await cachedAxiosGet(moralisClient, url, {}, 30000);
      
      moralis = response.data;
      const pv =
        moralis?.usdPrice ??
        moralis?.priceUsd ??
        moralis?.price_usd ??
        moralis?.price ??
        null;
      if (isFinite(Number(pv)) && Number(pv) > 0) return moralis;
    } catch {}

    const dsUrl = `https://api.dexscreener.com/latest/dex/tokens/${encodeURIComponent(
      tokenAddress
    )}`;
    const dsRes = await fetch(dsUrl);
    if (dsRes.ok) {
      const j = await dsRes.json();
      const pairs = Array.isArray(j?.pairs) ? j.pairs : [];
      if (pairs.length) {
        pairs.sort(
          (a, b) =>
            (Number(b?.liquidity?.usd || 0) - Number(a?.liquidity?.usd || 0)) ||
            (Number(b?.volume?.h24 || 0) - Number(a?.volume?.h24 || 0))
        );
        const best = pairs[0];
        const priceUsd = Number(best?.priceUsd ?? 0);
        const change24 =
          Number(best?.priceChange?.["24h"] ?? best?.priceChange?.h24 ?? 0);
        return { usdPrice: priceUsd, usdPrice24hrPercentChange: change24 };
      }
    }
    const gtSlug =
      {
        ethereum: "eth",
        eth: "eth",
        bsc: "bsc",
        binance: "bsc",
        polygon: "polygon",
        arbitrum: "arbitrum",
        base: "base",
        avalanche: "avalanche",
        optimism: "optimism",
        linea: "linea",
        fantom: "fantom",
        ronin: "ronin",
        pulse: "pulsechain",
        pulsechain: "pulsechain",
        solana: "solana",
      }[String(chainId || "").toLowerCase()] || null;
    if (gtSlug) {
      const gtUrl = `https://api.geckoterminal.com/api/v2/networks/${gtSlug}/tokens/${encodeURIComponent(
        tokenAddress
      )}`;
      const gtRes = await fetch(gtUrl);
      if (gtRes.ok) {
        const g = await gtRes.json();
        const attrs = g?.data?.attributes || {};
        const priceUsd = Number(attrs?.price_usd ?? 0);
        const change24 = Number(attrs?.price_change_24h ?? 0);
        return { usdPrice: priceUsd, usdPrice24hrPercentChange: change24 };
      }
    }
    return moralis;
  } catch (error) {
    console.error("Error fetching token price:", error);
    throw error;
  }
};

/**
 * Helper to format chain ID for API
 * @param {string} chainId - Chain ID in URL format
 * @returns {string} Chain ID in API format
 */
const formatChainForApi = (chainId) => {
  // Map URL format to API format
  const chainMap = {
    ethereum: "0x1",
    eth: "0x1",
    bsc: "0x38",
    polygon: "0x89",
    arbitrum: "0xa4b1",
    optimism: "0xa",
    base: "0x2105",
    avalanche: "0xa86a",
    linea: "0xe708",
    fantom: "0xfa",
    pulse: "0x171",
    ronin: "0x7e4",
    solana: "solana",
  };

  return chainMap[chainId] || chainId;
};

export const getWalletNetWorth = async (address) => {
  try {
    // Construct query parameters for all supported chains
    const chains = [
      "eth",
      "bsc",
      "polygon",
      "arbitrum",
      "avalanche",
      "optimism",
      "linea",
      "pulse",
      "ronin",
      "base",
      "fantom",
    ];
    const chainParams = chains
      .map((chain, index) => `chains[${index}]=${chain}`)
      .join("&");

    const url = `https://deep-index.moralis.io/api/v2.2/wallets/${address}/net-worth?${chainParams}&exclude_spam=true&exclude_unverified_contracts=true`;

    const response = await cachedAxiosGet(moralisClient, url, {}, 30000);
    return response.data;
  } catch (error) {
    console.error("Error fetching wallet net worth:", error);
    throw error;
  }
};

export const getWalletTokens = async (address, chain) => {
  try {
    const url = `https://deep-index.moralis.io/api/v2.2/wallets/${address}/tokens?chain=${chain}`;

    const response = await cachedAxiosGet(moralisClient, url, {}, 30000);
    return response.data;
  } catch (error) {
    console.error("Error fetching wallet tokens:", error);
    throw error;
  }

  
};

// src/services/api.js (append)
export async function getAveTrending({ chain = 'all', timeframe = '24h', page = 1, limit = 100 } = {}) {
  const qs = new URLSearchParams({ chain, timeframe, page, limit }).toString();
  const url = `${BACKEND_BASE}/api/ave/trending?${qs}`;
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw new Error('Failed to load Ave trending');
  const j = await r.json();
  return Array.isArray(j?.items) ? j.items : [];
}

// src/services/api.js (APPEND)
export async function getAveTrendingAll({ timeframe = '24h', limitPerChain = 50 } = {}) {
  const qs = new URLSearchParams({ timeframe, limitPerChain }).toString();
  const url = `${BACKEND_BASE}/api/ave/trending-all?${qs}`;
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw new Error('Failed to load Ave trending all chains');
  const j = await r.json();
  return Array.isArray(j?.items) ? j.items : [];
}

export async function enrichDsChange(items, max = 60) {
  const url = `${API_BASE}/api/ds/enrich-change`;
  const r = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ items, max }),
    credentials: "include",
  });
  if (!r.ok) throw new Error(`enrich failed: ${r.status}`);
  const j = await r.json();
  return Array.isArray(j?.items) ? j.items : [];
}
